import { Box, Grid, Stack, styled } from '@mui/material';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import { ClientBulkUploadDTO, getIndustries, IndustryDTO, uploadBulkClients } from 'wisestep-api';
import {
  WsButton,
  WsButtonGroup,
  WsColors,
  WsDataTable,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { BulkImportContext } from '../../context/BulkImportContext';

const StyledStack = styled(Stack)(() => ({
  width: 400,
}));

interface ISpreadSheetProps {
  parsedData: ClientBulkUploadDTO[];
  setNewClientPayload: Dispatch<SetStateAction<ClientBulkUploadDTO[]>>;
}

const MapSpreadsheetColumns = ({ parsedData, setNewClientPayload }: ISpreadSheetProps) => {
  const { setBulkImport } = useContext(BulkImportContext);
  const [ignoreFirstRow, setIgnoreFirstRow] = useState(true);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [industries, setIndustries] = useState<IndustryDTO[]>([]);

  const mandatoryFields = [
    'name',
    'linkedinUrl',
    'location',
    'clientStatus',
    'ownerEmail',
    'ownerName',
    'firstName',
    'lastName',
    'contactStatus',
    'emailAddress',
    'linkedinProfile',
    'contactOwnerEmail',
    'contactOwnerName',
  ];

  const methods = useForm();
  const navigate = useNavigate();
  const options: WsSelectObj[] = [
    {
      label: 'Company Name*',
      value: 'name',
    },
    {
      label: 'Linkedin URL*',
      value: 'linkedinUrl',
    },
    {
      label: 'Location*',
      value: 'location',
    },
    {
      label: 'Client Status*',
      value: 'clientStatus',
    },
    {
      label: 'Industry',
      value: 'industry',
    },
    {
      label: 'Source',
      value: 'source',
    },
    {
      label: 'Tags',
      value: 'tags',
    },
    {
      label: 'Description',
      value: 'description',
    },
    {
      label: 'Client Owner (Email)*',
      value: 'ownerEmail',
    },
    {
      label: 'Client Owner (Name)*',
      value: 'ownerName',
    },
    {
      label: 'Client Allowed Recruiters',
      value: 'allowedRecruiters',
    },
    {
      label: 'Client Unique ID',
      value: 'uniqueId',
    },
    {
      label: 'First Name*',
      value: 'firstName',
    },
    {
      label: 'Last Name*',
      value: 'lastName',
    },
    {
      label: 'Position',
      value: 'position',
    },
    {
      label: 'Contact Status*',
      value: 'contactStatus',
    },
    {
      label: 'Email Address*',
      value: 'emailAddress',
    },
    {
      label: 'Phone Number',
      value: 'phoneNumber',
    },
    {
      label: 'Contact Location',
      value: 'contactLocation',
    },
    {
      label: 'Contact Linkedin Profile*',
      value: 'linkedinProfile',
    },
    {
      label: 'Contact Tags',
      value: 'contactTags',
    },
    {
      label: 'Contact Owner (Email)*',
      value: 'contactOwnerEmail',
    },
    {
      label: 'Contact Owner (Name)*',
      value: 'contactOwnerName',
    },
    {
      label: 'Contact Allowed Recruiters',
      value: 'contactAllowedRecruiters',
    },
  ];

  const columns = Object.keys(parsedData[0])
    .filter(
      (value) =>
        [
          'column1',
          'column2',
          'column3',
          'column4',
          'column5',
          'column6',
          'column7',
          'column8',
          'column9',
          'column10',
          'column11',
          'column12',
          'column13',
          'column14',
          'column15',
          'column16',
          'column17',
          'column18',
          'column19',
          'column20',
          'column21',
          'column22',
          'column23',
          'column24',
        ].indexOf(value) !== -1
    )
    .map((key) => ({
      Header: <WsSelect name={key} label={''} selectArray={options} />,
      accessor: key,
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.BLACK} variant={WsTypoVariants.BODY1}>
          {cell.value}
        </WsTypography>
      ),
    }));

  useEffect(() => {
    const getIndustryData = async () => {
      const res = await getIndustries();
      setIndustries(res.data);
    };
    getIndustryData();
  }, []);

  const onSubmit = async (data) => {
    const modifiedData: ClientBulkUploadDTO[] = JSON.parse(JSON.stringify(parsedData));

    modifiedData.forEach((client) => {
      for (const key in data) {
        client[data[key]] = client[key];
      }
    });

    modifiedData.forEach((client) => {
      [
        'column1',
        'column2',
        'column3',
        'column4',
        'column5',
        'column6',
        'column7',
        'column8',
        'column9',
        'column10',
        'column11',
        'column12',
        'column13',
        'column14',
        'column15',
        'column16',
        'column17',
        'column18',
        'column19',
        'column20',
        'column21',
        'column22',
        'column23',
        'column24',
      ].forEach((e) => delete client[e]);
      setNewClientPayload(modifiedData);
    });

    try {
      const finalList = [...modifiedData];
      if (finalList && finalList.length > 0) {
        if (ignoreFirstRow) {
          finalList.shift();
        }

        const hasMissingFields = checkMandatoryFields(finalList);
        if (hasMissingFields) {
          setNotification(failed(`Mandatory fields are missing.`));
          setNewClientPayload([]);
          return;
        }

        if (!hasMissingFields) {
          const hasDuplicateClients = checkDuplicateClients(finalList);
          if (hasDuplicateClients) {
            setNotification(failed(`Duplicate client details are found.`));
            setNewClientPayload([]);
            return;
          }
        }

        finalList.forEach((client) => {
          if (client.industry) {
            client.industry =
              industries.find((industry) => industry.label === client.industry)?.value || null;
          }
        });
        const response = await uploadBulkClients(finalList);
        setNotification(success(`Client details successfully uploaded.`));
        if (setBulkImport) {
          setBulkImport(response.data);
          navigate('/clients');
        }
      } else {
        setNotification(failed('Please select at least one client to upload.'));
      }
    } catch (e) {
      setNotification(failed(`Something went wrong while uploading clients details.`));
    }
  };

  const checkMandatoryFields = (clientList: ClientBulkUploadDTO[]): boolean => {
    let hasMissingFields = false;
    for (const client of clientList) {
      for (const field of mandatoryFields) {
        if (!client[field] || client[field].trim() === '') {
          hasMissingFields = true;
          break;
        }
      }
      if (hasMissingFields) {
        break;
      }
    }
    return hasMissingFields;
  };

  const checkDuplicateClients = (clientList: ClientBulkUploadDTO[]): boolean => {
    const uniqueClients = new Set();
    for (const client of clientList) {
      const clientKey = `${client.name}-${client.emailAddress}`;
      if (uniqueClients.has(clientKey)) {
        return true;
      }
      uniqueClients.add(clientKey);
    }
    return false;
  };

  const onChangeRow = (index: number, value: string) =>
    value.toLocaleLowerCase() == 'yes' ? setIgnoreFirstRow(true) : setIgnoreFirstRow(false);

  return (
    <FormProvider {...methods}>
      <Box component="form">
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          my={5}
          sx={{ backgroundColor: WsColors.White }}
          p={2}
        >
          <StyledStack sx={{ width: 200 }} spacing={1}>
            <WsButtonGroup
              inputTitle={'Ignore First Row Data'}
              buttonsList={['Yes', 'No']}
              selectedIndexValue={onChangeRow}
              defaultValue={0}
            />
          </StyledStack>
        </Grid>
        <WsDataTable
          columns={columns}
          data={parsedData as []}
          boldItems={['column1']}
          headless={false}
          preSelectedRows={[]}
        />

        <Stack spacing={1} width="100%" direction="row" justifyContent="end" mt={2}>
          <WsButton variant="contained" onClick={() => navigate('/consultant/')} size="large">
            Cancel
          </WsButton>

          <WsButton
            variant="contained"
            onClick={() => navigate('/clients/import-excel')}
            size="large"
          >
            Back
          </WsButton>

          <WsButton
            variant="contained"
            type="button"
            onClick={() => onSubmit(methods.getValues())}
            size="large"
          >
            Next
          </WsButton>
        </Stack>
      </Box>
    </FormProvider>
  );
};

export default MapSpreadsheetColumns;
